import React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController } from 'react-hook-form';
import { Select } from '@/components/UI/Select/Select';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import * as S from '@/components/UI/Dropdown/SelectDropdownMenu.styles';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import useScreenSize from '@/hooks/useScreenSize';
import { ContactType } from '@/api/types/ads.types';
import { getContactTypeOptions } from '@/components/AdForm/components/ContactTypeFormSelect/ContactTypeFormSelect.constants';
import { ContactTypeFormSelectProps } from '@/components/AdForm/components/ContactTypeFormSelect/ContactTypeFormSelect.types';

export const ContactTypeFormSelect = <T extends FieldValues>({
  name,
  control,
}: ContactTypeFormSelectProps<T>) => {
  const { t } = useTranslation();
  const { isLarge } = useScreenSize();
  const [isOpen, toggle] = useToggleOpenState();
  const {
    field: { value, onChange },
    fieldState,
  } = useController({ name, control });
  const contactTypeOptions = getContactTypeOptions(t);
  const visibleValue = contactTypeOptions.find(
    option => option.value === value
  )?.label;

  function handleChange(val: ContactType) {
    onChange(val);
    toggle(false);
  }

  return (
    <Select
      name={name}
      isOpen={isOpen}
      toggle={toggle}
      isRequired
      appearance="filled"
      value={value ?? null}
      visibleValue={visibleValue}
      label={t('pages.new_ad.contact.contact_type')}
      helperText={fieldState.error?.message}
      hasError={!!fieldState.error}
      hasDropdown={isLarge}
    >
      {isLarge ? (
        isOpen && (
          <S.SelectDropdownMenu>
            <S.SelectDropdownMenuContainer $hasYPadding>
              {contactTypeOptions.map(option => (
                <S.SelectDropdownMenuItem
                  key={option.label}
                  onChangeHook={() => handleChange(option.value)}
                  value={option.value === value}
                  label={option.label}
                  appearance="radio"
                  size="sm"
                  highlightLabel
                  hasBorderBottom={false}
                  dataTestId={`contact-type-option-${option.value}`}
                />
              ))}
            </S.SelectDropdownMenuContainer>
          </S.SelectDropdownMenu>
        )
      ) : (
        <BottomSheet
          isOpen={isOpen}
          close={() => toggle(false)}
          title={t('pages.new_ad.contact.contact_type')}
          hasHeadBorder={false}
        >
          {contactTypeOptions.map(option => (
            <Checkbox
              key={option.label}
              onChangeHook={() => handleChange(option.value)}
              value={option.value === value}
              label={option.label}
              appearance="radio"
              size="lg"
              highlightLabel
              dataTestId={`contact-type-option-${option.value}`}
            />
          ))}
        </BottomSheet>
      )}
    </Select>
  );
};

ContactTypeFormSelect.displayName = 'ContactTypeFormSelect';
